import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';

type ConnectionStatus = 'good' | 'fair' | 'poor' | 'offline';

interface NetworkState {
  ping: number;
  status: ConnectionStatus;
  lastChecked: Date | null;
}

export default function NetworkMonitor() {
  const [state, setState] = useState<NetworkState>({
    ping: 0,
    status: 'good',
    lastChecked: null,
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const checkHealth = useCallback(async () => {
    // Respect the browser's native offline flag first — avoids the 'ping illusion'
    // where localhost responds instantly even when the browser reports no network.
    if (!navigator.onLine) {
      setState({ ping: 0, status: 'offline', lastChecked: new Date() });
      return;
    }

    const start = performance.now();
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout

      const res = await fetch('/api/health', {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!res.ok) throw new Error('Health check failed');

      const latency = Math.round(performance.now() - start);

      let status: ConnectionStatus;
      if (latency < 100) status = 'good';
      else if (latency < 300) status = 'fair';
      else status = 'poor';

      setState({ ping: latency, status, lastChecked: new Date() });
    } catch (err) {
      setState({ ping: 0, status: 'offline', lastChecked: new Date() });
    }
  }, []);

  useEffect(() => {
    // Initial check
    checkHealth();

    // Interval check every 4 seconds
    intervalRef.current = setInterval(checkHealth, 4000);

    // React immediately to browser online/offline events — don't wait for next tick
    const onOnline  = () => void checkHealth();
    const onOffline = () => setState({ ping: 0, status: 'offline', lastChecked: new Date() });
    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [checkHealth]);

  const getStatusConfig = (status: ConnectionStatus) => {
    switch (status) {
      case 'good':    return { bars: 4, barColor: 'bg-emerald-500', textColor: 'text-emerald-600', label: 'Good' };
      case 'fair':    return { bars: 3, barColor: 'bg-sky-500',     textColor: 'text-sky-600',     label: 'Fair' };
      case 'poor':    return { bars: 2, barColor: 'bg-amber-500',   textColor: 'text-amber-600',   label: 'Poor' };
      case 'offline': return { bars: 0, barColor: 'bg-red-500',     textColor: 'text-red-500',     label: 'Off'  };
    }
  };

  const config = getStatusConfig(state.status);

  const BAR_HEIGHTS = ['h-[4px]', 'h-[7px]', 'h-[10px]', 'h-[13px]'];

  return (
    <div className="relative">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex flex-col items-center justify-center gap-0.5 cursor-pointer select-none"
        title={`${config.label} — ${state.status === 'offline' ? 'Offline' : state.ping + 'ms'}`}
      >
        {/* Wifi signal bars (4 bars, bottom-aligned) */}
        <div className="flex items-end gap-[3px] h-[14px]">
          {BAR_HEIGHTS.map((h, i) => (
            <div
              key={i}
              className={`w-[4px] rounded-sm transition-all duration-500 ${
                i < config.bars
                  ? `${config.barColor} opacity-100`
                  : 'bg-slate-200 opacity-60'
              } ${h}`}
            />
          ))}
        </div>
        {/* Ping value */}
        <span className={`text-[9px] font-mono font-black leading-none ${config.textColor}`}>
          {state.status === 'offline' ? '✕' : `${state.ping}ms`}
        </span>
      </button>

      {/* Expanded Details Tooltip */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 10 }}
            className="absolute bottom-full right-0 mb-3 p-3 rounded-xl bg-white/95 backdrop-blur-xl border border-slate-200 shadow-2xl min-w-[160px] z-[200]"
          >
            <div className={`text-[10px] font-black uppercase tracking-wider mb-2 ${config.textColor}`}>
              {config.label} Connection
            </div>
            <div className="space-y-1 text-[10px] text-slate-500">
              <div className="flex justify-between">
                <span>Latency</span>
                <span className="font-mono font-bold text-slate-700">
                  {state.status === 'offline' ? 'N/A' : `${state.ping}ms`}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Last Check</span>
                <span className="font-mono font-bold text-slate-700">
                  {state.lastChecked
                    ? state.lastChecked.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
                    : '--'}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
